import React from "react";
import {
    CellContext,
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    Row,
    RowData,
    useReactTable
} from "@tanstack/react-table";
import {
    Collapse,
    IconButton,
    SxProps,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Theme
} from "@mui/material";
import {useTheme as useMuiTheme} from "@mui/material/styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {TooltipContent} from "@/lib/components/TooltipContent";
import {color_blue, color_grey} from "@/agentur/AgenturColors";
import {SortDirection} from "@/common/overview-state/overview-state-model";
import {resolveHeaderCellSx} from "@/lib/components/table-header-styles";
import {DataTableColumnHeader} from "@/lib/components/DataTableColumnHeader";

/**
 * Interface representing an identifiable entity.
 */
interface Identifiable {
    id?: number;
}

/**
 * Module augmentation instead of a locally exported `ColumnMeta` — TanStack declares `ColumnMeta<TData,
 * TValue>` as an EMPTY interface (`@tanstack/table-core`), so TypeScript accepts any object literal
 * against it and an export of a local interface would give no additional type checking (see #1607's plan,
 * "Der Metadatentyp der Spalten wird per Modul-Erweiterung deklariert, nicht exportiert"). Augmenting the
 * library's own interface here makes every `columns: ColumnDef<…>[]` call site in the codebase get real
 * checking on `meta.sortField` — a wrong value type (e.g. `meta: {sortField: 123}`) fails with TS2322, and
 * a misspelled property name (e.g. `meta: {sortFielddd: "..."}`) fails with TS2561 — without any cast at
 * the read site below.
 */
declare module "@tanstack/react-table" {
    interface ColumnMeta<TData extends RowData, TValue> {
        disableTooltip?: boolean;
        sortField?: string;
        /**
         * The column's identity for `renderColumnFilter` / `activeFilterColumnIds` (plan "Spaltenkopf-
         * Filterung und Sortieranzeige", Standing constraint 2) — NOT wired to any filter content yet in
         * Phase 1.2, only the `ColumnMeta` field itself. Several columns may deliberately share one
         * `filterColumnId` (e.g. two boolean fields filed under one "Info" column).
         */
        filterColumnId?: string;
    }
}

/**
 * Props for the `DataTable` component, which is a table abstraction for rendering and managing data rows with various features.
 *
 * @template TData - The type of the data rows, extending the `Identifiable` interface.
 *
 * @property {ColumnDef<TData, any>[]} columns - Defines the column structure of the table, including headers and cells.
 * @property {TData[]} data - The array of data rows to be displayed within the table.
 * @property {string} noResultsValue - A message or value displayed when there is no data to show in the table.
 * @property {(row: TData) => void} [onRowClick] - Optional callback triggered when a row is clicked.
 * @property {(row: TData) => void} [onRowDoubleClick] - Optional callback triggered when a row is double-clicked.
 * @property {number | null} [selectedRowId] - The identifier of the currently selected row, or null if no row is selected.
 * @property {(row: TData) => boolean} [isRowSelectable] - Determines if a specific row is selectable. Returns a boolean.
 * @property {boolean} [expandable] - Indicates if the rows in the table can be expanded to show additional content or controls.
 * @property {(row: Row<TData>) => React.ReactNode} [renderRowSubComponent] - Optional function to render custom content for an expanded row.
 * @property {string | number} [maxHeight] - Sets the maximum height of the table container for scrollable behavior.
 * @property {boolean} [striped] - If true, applies striped styling to alternate rows in the table for better readability.
 * @property {boolean} [disableHover] - If true, disables hover effects for rows in the table.
 * @property {React.RefObject<HTMLDivElement>} [scrollRef] - A ref attached to the scrollable container of the table for external control.
 * @property {SxProps<Theme>} [sx] - Custom styling applied to the table or its container, based on Material-UI's `SxProps` type.
 * @property {string} [sortField] - The currently active sort field, matched against a column's `meta.sortField` (#1607).
 * @property {SortDirection} [sortDirection] - The currently active sort direction, meaningful only together with `sortField`.
 * @property {(field: string | undefined, direction: SortDirection | undefined) => void} [onSortChange] - Called with the
 *   `{sortField, sortDirection}` pair for the sort entry chosen in a sortable column header's dropdown (`sortMenuChoice`).
 * @property {(columnId: string) => React.ReactNode} [renderColumnFilter] - Renders the filter controls for a column,
 *   looked up by its `meta.filterColumnId` (plan "Spaltenkopf-Filterung und Sortieranzeige", Standing constraint 2).
 *   Unused in Phase 1.2: no column sets `meta.filterColumnId` yet.
 * @property {ReadonlySet<string>} [activeFilterColumnIds] - Column ids currently carrying a deviating filter value,
 *   drives the header's filter indicator (Standing constraint 11). Unused in Phase 1.2, same reason as above.
 */
interface DataTableProps<TData extends Identifiable> {
    columns: ColumnDef<TData, any>[];
    data: TData[];
    noResultsValue: string;
    onRowClick?: (row: TData) => void;
    onRowDoubleClick?: (row: TData) => void;
    selectedRowId?: number | null;
    isRowSelectable?: (row: TData) => boolean;
    expandable?: boolean;
    renderRowSubComponent?: (row: Row<TData>) => React.ReactNode;
    maxHeight?: string | number;
    striped?: boolean;
    disableHover?: boolean;
    scrollRef?: React.RefObject<HTMLDivElement>;
    sx?: SxProps<Theme>;
    sortField?: string;
    sortDirection?: SortDirection;
    onSortChange?: (field: string | undefined, direction: SortDirection | undefined) => void;
    renderColumnFilter?: (columnId: string) => React.ReactNode;
    activeFilterColumnIds?: ReadonlySet<string>;
}

/**
 * Resolves the row-hover background color — extracted out of a nested ternary (SonarQube `typescript:S3358`)
 * that pre-dated #1607 but sits in a file this phase already touches (see the plan's Quality Gate:
 * MAJOR findings on a touched file are fixed in the same phase, not deferred).
 */
function resolveRowHoverBackgroundColor(disableHover: boolean, mode: "light" | "dark"): string | undefined {
    if (disableHover) {
        return undefined;
    }
    return mode === "dark" ? color_grey[0] : color_blue[4];
}

/**
 * Resolves the `aria-sort` value for a sortable column header. `"none"` when this particular column is
 * not the currently active one. Only called for columns that declare `meta.sortField`, a purely
 * filterable column renders its `TableCell` with no `aria-sort` attribute at all.
 */
function resolveAriaSort(isActive: boolean, direction: SortDirection | undefined): "ascending" | "descending" | "none" {
    if (!isActive) {
        return "none";
    }
    return direction === "DESC" ? "descending" : "ascending";
}

/**
 * Represents a reusable and flexible table component with features such as row selection, expandability,
 * customizability for styles and behavior, and rendering subcomponents within rows.
 *
 * @template TData
 * @param {Object} props - The properties for configuring the DataTable component.
 * @param {Array} props.columns - An array defining the structure and properties of the table's columns.
 * @param {Array<TData>} props.data - The data to be displayed in the table.
 * @param {string | React.ReactNode} [props.noResultsValue] - The content to display when no data is available.
 * @param {Function} [props.onRowClick] - Callback function triggered when a row is clicked. Receives the clicked row's data as an argument.
 * @param {Function} [props.onRowDoubleClick] - Callback function triggered when a row is double-clicked. Receives the double-clicked row's data as an argument.
 * @param {string | number} [props.selectedRowId] - The ID of the currently selected row, if any.
 * @param {Function} [props.isRowSelectable] - A function to determine if a specific row can be selected. Receives the row's data as an argument and returns a boolean.
 * @param {boolean} [props.expandable=false] - Determines if the rows in the table can be expanded to show additional details.
 * @param {Function} [props.renderRowSubComponent] - A function to render a subcomponent for expanded rows. Receives the row's data as an argument.
 * @param {string | number} [props.maxHeight] - The maximum height of the table, enabling vertical scrollbar if content overflows.
 * @param {boolean} [props.striped=false] - Determines if alternating row styling should be applied for better readability.
 * @param {boolean} [props.disableHover=false] - Disables the hover styling for rows if set to true.
 * @param {React.Ref} [props.scrollRef] - A reference for the table's scrollable container.
 * @param {Object} [props.sx] - Custom styles to be applied to the table container using the Material-UI styling system.
 * @returns {React.ReactElement} A table component rendered with the provided configurations and functionality.
 */
export const DataTable = <TData extends Identifiable>({
                                                          columns,
                                                          data,
                                                          noResultsValue,
                                                          onRowClick,
                                                          onRowDoubleClick,
                                                          selectedRowId,
                                                          isRowSelectable,
                                                          expandable = false,
                                                          renderRowSubComponent,
                                                          maxHeight,
                                                          striped = false,
                                                          disableHover = false,
                                                          scrollRef,
                                                          sx,
                                                          sortField,
                                                          sortDirection,
                                                          onSortChange,
                                                          renderColumnFilter,
                                                          activeFilterColumnIds
                                                      }: DataTableProps<TData>): React.ReactElement => {
    const theme = useMuiTheme();

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getRowCanExpand: () => expandable
    });

    const msx = {
        ...sx,
        borderRadius: "4px",
        maxHeight,
        "& td": {
            borderBottom: "none",
            paddingTop: 1.5,
            paddingBottom: 1.5
            // marginLeft: 4,
            // marginRight: 4
        },
        "& th": {
            // borderBottom: "none",
            paddingTop: 1,
            paddingBottom: 1
            // marginLeft: 4,
            // marginRight: 4
        },
        "& tr:hover, & tr:hover td": {
            backgroundColor: resolveRowHoverBackgroundColor(disableHover, theme.palette.mode),
            cursor: onRowClick ? "pointer" : undefined
        },

        // Inner table with border
        "& table td:has(table)": {
            padding: 0
        },
        "& table table td": {
            paddingLeft: 1,
            paddingRight: 1,
            paddingTop: 0,
            paddingBottom: 0,
            textAlign: "right"
            // borderLeft: "1px solid",
            // borderTop: "1px solid",
            // borderBottom: "1px solid"
        },
        // "& table table td:last-child": {
        // borderRight: "1px solid"
        // },
        "& table table tr:not(:last-child)>td": {
            // borderRight: "1px solid"
            borderBottom: "1px solid"
        }
        // "& table td:has(table):last-child": {
        // paddingRight: 2
        // }
    };

    return (
            <TableContainer ref={scrollRef} sx={msx}>
                <Table stickyHeader>
                    <TableHead>
                        {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    {expandable && (
                                            <TableCell
                                                    style={{
                                                        backgroundColor: theme.palette.mode === "dark" ? color_blue[3] : color_blue[2],
                                                        width: "50px"
                                                    }}
                                            />
                                    )}
                                    {headerGroup.headers.map((header) => {
                                        const headerContent = header.isPlaceholder
                                                ? null
                                                : flexRender(header.column.columnDef.header, header.getContext());
                                        const headerCellSx: SxProps<Theme> = {
                                            backgroundColor: theme.palette.mode === "dark" ? color_blue[3] : color_blue[2],
                                            fontWeight: "bold",
                                            // maxWidth: "200px",
                                            minWidth: header.column.columnDef.minSize,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            ...resolveHeaderCellSx(theme.palette.mode)
                                        };
                                        // `columnSortField` stays `undefined` for a table with no `onSortChange` at
                                        // all, even if a column happens to declare `meta.sortField` — mirrors the
                                        // pre-Phase-1.2 guard (`columnSortField !== undefined && onSortChange`), so a
                                        // table wired for neither sorting nor filtering renders every header as
                                        // plain text, unchanged (checked: `onSortChange` has exactly two call sites,
                                        // both of which also pass it).
                                        const columnSortField = onSortChange ? header.column.columnDef.meta?.sortField : undefined;
                                        const columnFilterId = header.column.columnDef.meta?.filterColumnId;
                                        // Not exercised in Phase 1.2: no column sets `meta.filterColumnId` yet, so
                                        // `renderColumnFilter` is never invoked here until Milestone 2/3 wire it up.
                                        const filterContent =
                                                columnFilterId !== undefined ? renderColumnFilter?.(columnFilterId) : undefined;
                                        const hasActiveFilter =
                                                columnFilterId !== undefined && (activeFilterColumnIds?.has(columnFilterId) ?? false);

                                        if (columnSortField !== undefined || filterContent !== undefined) {
                                            const isActive = columnSortField !== undefined && sortField === columnSortField;
                                            return (
                                                    <TableCell
                                                            key={header.id}
                                                            aria-sort={
                                                                    columnSortField !== undefined
                                                                            ? resolveAriaSort(isActive, sortDirection)
                                                                            : undefined
                                                            }
                                                            sx={headerCellSx}
                                                    >
                                                        <DataTableColumnHeader
                                                                label={headerContent}
                                                                mode={theme.palette.mode}
                                                                columnSortField={columnSortField}
                                                                appliedSortField={sortField}
                                                                appliedSortDirection={sortDirection}
                                                                onSortChange={onSortChange}
                                                                filterContent={filterContent}
                                                                hasActiveFilter={hasActiveFilter}
                                                        />
                                                    </TableCell>
                                            );
                                        }

                                        return (
                                                <TableCell key={header.id} sx={headerCellSx}>
                                                    {headerContent}
                                                </TableCell>
                                        );
                                    })}
                                </TableRow>
                        ))}
                    </TableHead>
                    <TableBody>
                        {table.getRowModel().rows.length > 0 ? (
                                table.getRowModel().rows.map((row, index) => (
                                        <React.Fragment key={row.id}>
                                            <TableRow
                                                    onClick={() => {
                                                        if (isRowSelectable && !isRowSelectable(row.original)) {
                                                            return;
                                                        }
                                                        onRowClick?.(row.original);
                                                    }}
                                                    onDoubleClick={() => {
                                                        if (isRowSelectable && !isRowSelectable(row.original)) {
                                                            return;
                                                        }
                                                        onRowDoubleClick?.(row.original);
                                                    }}
                                                    hover={!disableHover}
                                                    selected={selectedRowId === row.original.id}
                                                    style={{
                                                        cursor: (() => {
                                                            if (!expandable) {
                                                                return "default";
                                                            }
                                                            if (isRowSelectable && !isRowSelectable(row.original)) {
                                                                return "not-allowed";
                                                            }
                                                            return "pointer";
                                                        })(),
                                                        opacity: isRowSelectable && !isRowSelectable(row.original) ? 0.5 : 1,
                                                        // Apply selection styling first, then row striping if striped=true
                                                        backgroundColor: (() => {
                                                            if (selectedRowId === row.original.id) {
                                                                return theme.palette.mode === "dark" ? color_blue[0] : color_blue[3];
                                                            }
                                                            if (!striped) {
                                                                return "inherit";
                                                            }
                                                            if (index % 2 === 0) {
                                                                return theme.palette.mode === "dark"
                                                                        ? color_blue[1]
                                                                        : theme.palette.common.white;
                                                            }
                                                            return theme.palette.mode === "dark" ? color_blue[2] : color_grey[3];
                                                        })()
                                                    }}
                                            >
                                                {expandable && (
                                                        <TableCell>
                                                            <IconButton
                                                                    size="small"
                                                                    style={{margin: -3}}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        row.toggleExpanded();
                                                                    }}
                                                            >
                                                                {row.getIsExpanded() ? (
                                                                        <KeyboardArrowUpIcon/>
                                                                ) : (
                                                                        <KeyboardArrowDownIcon/>
                                                                )}
                                                            </IconButton>
                                                        </TableCell>
                                                )}
                                                {row.getVisibleCells().map((cell) => {
                                                    const cellContent = flexRender(cell.column.columnDef.cell, cell.getContext());

                                                    const disableTooltip = cell.column.columnDef.meta?.disableTooltip;

                                                    return (
                                                            <TableCell
                                                                    key={cell.id}
                                                                    style={{
                                                                        // maxWidth: "200px",
                                                                        whiteSpace: "nowrap",
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis"
                                                                    }}
                                                            >
                                                                {disableTooltip ? cellContent :
                                                                        <TooltipContent value={cellContent}/>}
                                                            </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                            {row.getIsExpanded() && (
                                                    <TableRow>
                                                        <TableCell
                                                                colSpan={expandable ? columns.length + 1 : columns.length}
                                                                style={{paddingBottom: 0, paddingTop: 0}}
                                                        >
                                                            <Collapse in={row.getIsExpanded()} timeout="auto"
                                                                      unmountOnExit>
                                                                {renderRowSubComponent?.(row)}
                                                            </Collapse>
                                                        </TableCell>
                                                    </TableRow>
                                            )}
                                        </React.Fragment>
                                ))
                        ) : (
                                <TableRow>
                                    <TableCell colSpan={columns.length + (expandable ? 1 : 0)} align="center">
                                        {noResultsValue}
                                    </TableCell>
                                </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
    );
};

/**
 * A function that creates a React element to render a cell with its content
 * right-aligned. This is typically used in a table or grid component where
 * numerical data or other right-aligned content is required.
 *
 * @param {CellContext<any, any>} info - The context object containing
 * information about the cell, including its value and related metadata.
 * @returns {React.ReactElement} A React element representing the cell with
 * its content aligned to the right.
 */
export const buildRightAlignedCell = (info: CellContext<any, any>): React.ReactElement => (
        <div style={{textAlign: "right"}}>{info.getValue() as number}</div>
);
